import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  forwardRef,
  HostBinding,
  HostListener,
  Input,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { CalendarPanelComponent } from '../calendar-panel/calendar-panel.component';

export interface DateRange {
  start: Date | null;
  end: Date | null;
}

@Component({
  selector: 'app-datepicker-range',
  templateUrl: './datepicker-range.component.html',
  styleUrls: ['./datepicker-range.component.scss'],
  standalone: true,
  imports: [CommonModule, CalendarPanelComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DatepickerRangeComponent),
      multi: true,
    },
  ],
})
export class DatepickerRangeComponent implements ControlValueAccessor {
  @Input() startPlaceholder = 'Data inicial';
  @Input() endPlaceholder = 'Data final';
  @Input() min: Date | null = null;
  @Input() max: Date | null = null;

  @HostBinding('class.app-datepicker-range') readonly hostClass = true;

  startDate: Date | null = null;
  endDate: Date | null = null;
  isDisabled = false;
  isOpen = false;
  viewYear = new Date().getFullYear();
  viewMonth = new Date().getMonth();
  selectionPhase: 'start' | 'end' = 'start';
  hoveredDate: Date | null = null;

  private onChange: (v: DateRange) => void = () => {};
  onTouched: () => void = () => {};

  constructor(private el: ElementRef, private cd: ChangeDetectorRef) {}

  get hasValue(): boolean {
    return this.startDate !== null || this.endDate !== null;
  }

  get isSelectingEnd(): boolean {
    return this.selectionPhase === 'end';
  }

  clear(): void {
    this.startDate = null;
    this.endDate = null;
    this.hoveredDate = null;
    this.selectionPhase = 'start';
    this.isOpen = false;
    this.emitValue();
    this.cd.markForCheck();
  }

  toggleDropdown(): void {
    if (this.isDisabled) return;
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      this.selectionPhase = 'start';
      this.hoveredDate = null;
      const ref = this.startDate || new Date();
      this.viewYear = ref.getFullYear();
      this.viewMonth = ref.getMonth();
    }
  }

  openForStart(): void {
    if (this.isDisabled) return;
    this.selectionPhase = 'start';
    this.hoveredDate = null;
    this.isOpen = true;
    const ref = this.startDate || new Date();
    this.viewYear = ref.getFullYear();
    this.viewMonth = ref.getMonth();
  }

  openForEnd(): void {
    if (this.isDisabled) return;
    if (this.startDate) {
      this.selectionPhase = 'end';
    } else {
      this.selectionPhase = 'start';
    }
    this.hoveredDate = null;
    this.isOpen = true;
    const ref = this.endDate || this.startDate || new Date();
    this.viewYear = ref.getFullYear();
    this.viewMonth = ref.getMonth();
  }

  onDateSelect(date: Date): void {
    if (this.selectionPhase === 'start') {
      this.startDate = date;
      this.endDate = null;
      this.selectionPhase = 'end';
      this.cd.markForCheck();
    } else {
      if (this.startDate && date < this.startDate) {
        this.startDate = date;
        this.endDate = null;
        this.cd.markForCheck();
      } else {
        this.endDate = date;
        this.isOpen = false;
        this.selectionPhase = 'start';
        this.hoveredDate = null;
        this.emitValue();
        this.cd.markForCheck();
      }
    }
  }

  onDateHover(date: Date): void {
    if (this.selectionPhase === 'end') {
      this.hoveredDate = date;
      this.cd.markForCheck();
    }
  }

  onViewMonthChange(month: number): void {
    this.viewMonth = month;
  }

  onViewYearChange(year: number): void {
    this.viewYear = year;
  }

  formatDate(date: Date): string {
    const d = String(date.getDate()).padStart(2, '0');
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const y = date.getFullYear();
    return `${d}/${m}/${y}`;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    if (!this.el.nativeElement.contains(event.target)) {
      this.isOpen = false;
    }
  }

  private emitValue(): void {
    this.onChange({ start: this.startDate, end: this.endDate });
    this.onTouched();
  }

  writeValue(val: DateRange | null): void {
    this.startDate = val?.start ?? null;
    this.endDate = val?.end ?? null;
    this.cd.markForCheck();
  }

  registerOnChange(fn: (v: DateRange) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
    this.cd.markForCheck();
  }
}
